import type { Block } from '../../functions/_lib/blocks';
import { newBlockId } from '../../functions/_lib/blocks';

export type Template = {
  id: string;
  name: string;
  description?: string;
  subject?: string;
  preheader?: string;
  blocks: Block[];
  createdAt?: string;
  version?: number;
};

const LEGACY_DRAFT_STORAGE_KEY = 'atomic-rose-newsletter-drafts';
const BUILTIN_OVERRIDE_KEY = 'atomic-rose-newsletter-builtin-overrides';

const builtInModules = import.meta.glob<Template>('../templates/*.json', { eager: true, import: 'default' });

function loadBuiltInOverrides(): Record<string, Template> {
  try {
    const raw = localStorage.getItem(BUILTIN_OVERRIDE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function loadBuiltInTemplates(): Array<Template & { _overridden?: boolean }> {
  const overrides = loadBuiltInOverrides();
  return Object.values(builtInModules).map((original) => {
    const id = original.id || 'builtin';
    if (overrides[id]) return { ...overrides[id], id, _overridden: true };
    return { ...original, id };
  });
}

export function saveBuiltInOverride(id: string, template: Omit<Template, 'id'>): void {
  const overrides = loadBuiltInOverrides();
  overrides[id] = { ...template, id };
  localStorage.setItem(BUILTIN_OVERRIDE_KEY, JSON.stringify(overrides));
}

export function resetBuiltInOverride(id: string): void {
  const overrides = loadBuiltInOverrides();
  delete overrides[id];
  localStorage.setItem(BUILTIN_OVERRIDE_KEY, JSON.stringify(overrides));
}

export function isBuiltInOverridden(id: string): boolean {
  return id in loadBuiltInOverrides();
}

export function isBuiltInId(id: string): boolean {
  return Object.values(builtInModules).some((t) => (t.id || 'builtin') === id);
}

type CloudDraft = {
  id: string;
  name: string;
  subject: string | null;
  preheader: string | null;
  blocks: Block[];
  sources: string[] | null;
  version: number;
  updatedAt: number;
};

function cloudToTemplate(d: CloudDraft): Template {
  return {
    id: d.id,
    name: d.name,
    subject: d.subject ?? undefined,
    preheader: d.preheader ?? undefined,
    blocks: d.blocks,
    createdAt: new Date(d.updatedAt).toISOString(),
    version: d.version,
  };
}

async function jsonRequest<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, init);
  const text = await res.text();
  let data: any;
  try { data = JSON.parse(text); }
  catch { throw new Error(`non_json_${res.status}`); }
  if (!res.ok || data?.ok === false) throw new Error(data?.error || `req_failed_${res.status}`);
  return data as T;
}

export async function loadDrafts(): Promise<Template[]> {
  const r = await jsonRequest<{ ok: true; drafts: CloudDraft[] }>('/api/drafts');
  return r.drafts.map(cloudToTemplate);
}

export async function saveDraft(template: Omit<Template, 'id' | 'createdAt'>): Promise<Template> {
  const r = await jsonRequest<{ ok: true; id: string; version: number }>('/api/drafts', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: template.name,
      subject: template.subject,
      preheader: template.preheader,
      blocks: template.blocks,
      expectedVersion: template.version ?? 0,
    }),
  });
  return {
    ...template,
    id: r.id,
    createdAt: new Date().toISOString(),
    version: r.version,
  };
}

export async function renameDraft(draft: Template, newName: string): Promise<void> {
  await jsonRequest<{ ok: true }>('/api/drafts', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      id: draft.id,
      name: newName,
      subject: draft.subject,
      preheader: draft.preheader,
      blocks: draft.blocks,
      expectedVersion: draft.version ?? 0,
    }),
  });
}

export async function duplicateDraft(draft: Template): Promise<void> {
  await saveDraft({
    name: `${draft.name} (copy)`,
    subject: draft.subject,
    preheader: draft.preheader,
    blocks: draft.blocks,
  });
}

export async function deleteDraft(id: string): Promise<void> {
  await jsonRequest<{ ok: true }>(`/api/drafts?id=${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// ---- Legacy localStorage drafts ----
//
// Older builds saved drafts to localStorage. We don't read these in the
// normal UI anymore, but the migration helper below lets the user upload
// them to the cloud once and then clear local storage.

type LegacyDraft = {
  id?: string;
  name?: string;
  subject?: string;
  preheader?: string;
  blocks?: Block[];
  createdAt?: string;
};

export function loadLocalLegacyDrafts(): Template[] {
  try {
    const raw = localStorage.getItem(LEGACY_DRAFT_STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return (parsed as LegacyDraft[])
      .filter((d) => d && Array.isArray(d.blocks))
      .map((d) => ({
        id: d.id || `legacy-${Date.now()}`,
        name: String(d.name || 'Untitled draft'),
        subject: d.subject,
        preheader: d.preheader,
        blocks: d.blocks!,
        createdAt: d.createdAt,
      }));
  } catch {
    return [];
  }
}

export function clearLocalLegacyDrafts(): void {
  localStorage.removeItem(LEGACY_DRAFT_STORAGE_KEY);
}

export async function migrateLocalDraftsToCloud(): Promise<{ migrated: number; failed: number }> {
  const local = loadLocalLegacyDrafts();
  let migrated = 0;
  let failed = 0;
  for (const d of local) {
    try {
      await saveDraft({
        name: d.name,
        subject: d.subject,
        preheader: d.preheader,
        blocks: d.blocks,
      });
      migrated++;
    } catch {
      failed++;
    }
  }
  if (failed === 0) clearLocalLegacyDrafts();
  return { migrated, failed };
}

export function downloadTemplateJson(template: Template): void {
  const safeName = template.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'template';
  const exportable: Template = {
    ...template,
    id: safeName,
    createdAt: undefined,
  };
  const blob = new Blob([JSON.stringify(exportable, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${safeName}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function parseImportedTemplate(json: string): Template {
  const parsed = JSON.parse(json);
  if (!parsed || typeof parsed !== 'object') throw new Error('not_an_object');
  if (!Array.isArray(parsed.blocks)) throw new Error('missing_blocks');
  return {
    id: parsed.id || `imported-${Date.now()}`,
    name: String(parsed.name || 'Imported template'),
    description: parsed.description,
    subject: parsed.subject,
    preheader: parsed.preheader,
    blocks: parsed.blocks,
    createdAt: parsed.createdAt,
  };
}

export function withFreshBlockIds(blocks: Block[]): Block[] {
  return blocks.map((b) => ({ ...b, id: newBlockId() } as Block));
}
